import {AfterViewInit, Component, OnChanges, OnDestroy, OnInit, SimpleChanges, TemplateRef, ViewChild} from '@angular/core';
import {
    CmContractObjectViewTypeUiEnum
} from "../../../../page/overview/cm-contract-page/data/cm-contract-object-view-type-ui.enum";
import {CmContractInlineObjectViewTypeUiEnum} from "./view/inline/view-type/view-type.enum";
import {ObjectBaseComponent} from "../../../../../../core/shared/object/mvs-object-base/object-base.component";
import {UiModeMeta} from "../../../../../../core/shared/object/type/object-ui-mode-meta.type";
import {UiViewType} from "../../../../../../core/shared/object/type/object-ui-view.type";
import {ObjectIdentifierData} from "../../../../../../core/shared/basic/object-identifier-data";
import {Popover} from "primeng/popover";
import {ObjectIdentifier} from "../../../../../../core/shared/basic/object-identifier";
import {Observable, Subscription} from "rxjs";
// TM-1000002050: debounceTime + filter for the object-bus subscription (VN-Wechsel, design §5 / §11 D-2)
import {debounceTime, filter, map} from "rxjs/operators";
import {ContractService} from "../../../../service/api/contract.service";
import {ContractDto} from "../../../../model/dto/entity/contract.dto";
import {ObjectRequestList} from "../../../../../../core/shared/dto/object/request/object-request-list";
import {FilterCriteria} from "../../../../../../core/shared/filter/api/filter.criteria";
import {DtoList} from "../../../../../../core/shared/dto/dto.list";
import {ObjectChangeInformation} from "../../../../../../core/shared/logic/object-change-information";
import {MvsObjectService} from "../../../../../../core/shared/service/object/mvs-object.service";

/**
 * TM-1000002050: the contract itself — announced by the „VN-Wechsel“ wizard state on the BASE object bus (WZ-022) after
 * its execute, and broadcast by {@code MvsCrudService} on every generic contract write.
 */
const CM_CONTRACT_OBJECT_TYPE_ALIAS = 'cm.Contract';

/** TM-1000002050: the main customer assignment — the „VN-Wechsel“ announces it by its id only (no contractDtoId). */
const CR_CUSTOMER_CONTRACT_OBJECT_TYPE_ALIAS = 'cr.CustomerContract';

/** TM-1000002050: one write raises several events (up to seven for a VN-Wechsel) — ONE reload per burst. */
const CM_CONTRACT_OBJECT_REFRESH_DEBOUNCE_MS = 300;


@Component({
    selector: 'cm-contract-object-component',
    templateUrl: 'contract-object.component.html',
    standalone: false
})
export class ContractObjectComponent extends ObjectBaseComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
    showCreateTicketOverlay: boolean;
    @ViewChild("op", {static: true}) createTicketOverlay: Popover;

    /**
     * Quick-actions trigger offered to the leading slot of the navigation icon row. Shown only where
     * the hosting {@code mvs-object} opts in (the object navigation panel).
     *
     * @ticket TM-1000002015
     */
    @ViewChild("navigationLeading", {static: true}) navigationLeadingTemplate: TemplateRef<any>;

    /**
     * Reloads the panel's contract on a contract / customer-assignment change — see subscribeToContractChanges().
     *
     * @ticket TM-1000002050
     */
    protected contractChangeSubscription: Subscription | null = null;

    ngOnInit() {
        super.ngOnInit();
        // TM-1000002050: the panel listens to the object bus itself (design §5, §11 D-2)
        this.subscribeToContractChanges();
    }

    /**
     * Reload THIS contract when it or its main customer assignment was written (design projects/vn_wechsel/
     * 02-wizard-design.md §5 „Lauschende Oberflächen“, §11 D-2).
     *
     * The panel is the second entry of the „VN-Wechsel“ (its quick actions lead the icon row) and loads its OWN dto
     * ({@link #getRetrieveObjectObservable}) — neither the contract page's refetch nor the standard navigation's widget
     * rebuild reaches it. The wizard announces its execute on the BASE {@code MvsObjectService} (WZ-022) and the quick
     * actions' close funnel no longer reloads anything (D-2), so without this subscription the panel would keep the
     * old policyholder until a manual reload.
     *
     * Filter by ALIAS, then by owner: {@code cm.Contract} by its own id (the successor's event is skipped — the
     * origin's event of the same announcement arrives too), {@code cr.CustomerContract} by {@code contractDtoId}, an
     * unknown owner refreshes (the wizard's stub carries only the assignment id). Debounced AFTER the filter (one write
     * raises several events), torn down in ngOnDestroy. Only where the panel offers the quick actions (full / side /
     * mini-side): inline and consultant render no quick actions, and an inline LIST of contracts would otherwise
     * reload every row on an id-only event.
     *
     * @ticket TM-1000002050
     */
    protected subscribeToContractChanges(): void {

        this.contractChangeSubscription?.unsubscribe();
        this.contractChangeSubscription = null;

        if (!this.isNavigationLeadingSupported()) {
            return;
        }

        const objectService: MvsObjectService = this.coreService.getObjectService();

        this.contractChangeSubscription = objectService.objectChangeEvent
            .pipe(
                filter((change: ObjectChangeInformation) => this.isOwnContractChange(change)),
                debounceTime(CM_CONTRACT_OBJECT_REFRESH_DEBOUNCE_MS))
            .subscribe(() => this.refreshObject(false));
    }

    /**
     * True for a {@code cm.Contract} event of THIS contract and for a {@code cr.CustomerContract} event of THIS
     * contract or of an unknown owner.
     *
     * @ticket TM-1000002050
     */
    protected isOwnContractChange(change: ObjectChangeInformation): boolean {

        const contractId = this.objectIdentifier?.objectId;

        if (!change || !contractId) {
            return false;
        }

        if (change.objectType === CM_CONTRACT_OBJECT_TYPE_ALIAS) {
            const changedId = change.after?.id;
            return changedId == null || changedId === contractId;
        }

        if (change.objectType === CR_CUSTOMER_CONTRACT_OBJECT_TYPE_ALIAS) {
            const changedContractId = change.after?.['contractDtoId'];
            return changedContractId == null || changedContractId === contractId;
        }

        return false;
    }

    /**
     * Offers the quick-actions template to the hosting {@code mvs-object}.
     *
     * @ticket TM-1000002015
     */
    ngAfterViewInit(): void {
        if (this.isNavigationLeadingSupported()) {
            this.onNavigationLeadingTemplate.emit(this.navigationLeadingTemplate);
        }
    }

    /**
     * The quick actions lead the icon row in the full/side/mini-side views only; inline and consultant
     * render no navigation of their own.
     *
     * @ticket TM-1000002015
     */
    isNavigationLeadingSupported(): boolean {
        return this.uiMode !== 'inline' && this.uiMode !== 'consultant';
    }

    /**
     * Loads the contract with partner and current main customer - the same request the contract
     * page uses - so the quick actions get the dto shape they are built for. Inline and consultant
     * keep the plain GET. An empty result is reported through the regular load-error path.
     *
     * @ticket TM-1000002015
     */
    protected override getRetrieveObjectObservable(): Observable<any> {

        if (!this.isNavigationLeadingSupported()) {
            return super.getRetrieveObjectObservable();
        }

        const contractService: ContractService = <ContractService>this.crudService;

        const dtoRequest: ObjectRequestList = new ObjectRequestList(
            false,
            [FilterCriteria.create('id', FilterCriteria.cOperatorEqual, this.objectIdentifier.objectId)],
            []);

        dtoRequest.objectRequestComplex = contractService.getContractWithPartnerAndCustomer();

        return contractService.list(dtoRequest).pipe(
            map((res: DtoList<ContractDto>) => {
                const dto: ContractDto = res?.entries?.[0];
                if (!dto) {
                    // routed into postRefreshObjectError() by the base retrieveObject()
                    throw {error: 'Contract not found.'};
                }
                return dto;
            }));
    }


    ngOnDestroy() {
        // TM-1000002050: tear the object-bus subscription down with the panel
        this.contractChangeSubscription?.unsubscribe();
        this.contractChangeSubscription = null;
        super.ngOnDestroy();
    }

    static getMeta(): UiModeMeta[] {

        const viewTypes:  UiViewType[] = [
            {
                key: CmContractObjectViewTypeUiEnum.standard,
                label: 'Standard',
                icon: 'pi pi-desktop',
                color: '#ffffff',
                backgroundColor: '#1f2937',
                description: 'Detailed view with full controls'
            },
            {
                key: CmContractObjectViewTypeUiEnum.documents,
                label: 'Documents',
                icon: 'pi pi-folder',
                color: '#1c64f2',
                backgroundColor: '#e0f2fe',
                description: 'View and manage related documents'
            },
            {
                key: CmContractObjectViewTypeUiEnum.sales,
                label: 'Vertriebsansicht',
                icon: 'pi pi-dollar',
                color: '#15803d',
                backgroundColor: '#dcfce7',
                description: 'Sales-related contract details'
            },
            {
                key: CmContractObjectViewTypeUiEnum.accounting,
                label: 'Accounting',
                icon: 'pi pi-calculator',
                color: '#7c3aed',
                backgroundColor: '#ede9fe',
                description: 'Financial and billing data'
            },
            {
                key: CmContractObjectViewTypeUiEnum.registration,
                label: 'Registration',
                icon: 'pi pi-pencil',
                color: '#ea580c',
                backgroundColor: '#ffedd5',
                description: 'Registration and sign-up details'
            },
            {
                key: CmContractObjectViewTypeUiEnum.documentUpload,
                label: 'Upload',
                icon: 'pi pi-upload',
                color: '#0e7490',
                backgroundColor: '#cffafe',
                description: 'Upload and manage attachments'
            },
            {
                key: CmContractObjectViewTypeUiEnum.objects,
                label: 'Objects',
                icon: 'pi pi-box',
                color: '#6b7280',
                backgroundColor: '#f3f4f6',
                description: 'Insurable objects and linked items'
            },
            {
                key: CmContractObjectViewTypeUiEnum.notifications,
                label: 'Notifications',
                icon: 'pi pi-bell',
                color: '#f59e0b',
                backgroundColor: '#fef9c3',
                description: 'Alerts and notifications related to this contract'
            },
            {
                key: CmContractObjectViewTypeUiEnum.genericTickets,
                label: 'Tickets',
                icon: 'pi pi-ticket',
                color: '#c026d3',
                backgroundColor: '#fce7f3',
                description: 'Linked service tickets or support cases'
            },
            {
                key: CmContractObjectViewTypeUiEnum.genericWorkflows,
                label: 'Workflows',
                icon: 'pi pi-share-alt',
                color: '#2563eb',
                backgroundColor: '#dbeafe',
                description: 'Automated workflows and transitions'
            },
            {
                key: CmContractObjectViewTypeUiEnum.calculator,
                label: 'Calculator',
                icon: 'pi pi-sliders-h',
                color: '#14b8a6',
                backgroundColor: '#ccfbf1',
                description: 'Contract-specific calculators or quote tools'
            }
        ]

        return [
            {
                mode: 'full',
                viewTypes: viewTypes
            },
            {
                mode: 'side',
                viewTypes: viewTypes
            },
            {
                mode: 'mini-side',
                viewTypes: viewTypes
            },
            {
                mode: 'inline',
                viewTypes: [
                    {
                        key: CmContractInlineObjectViewTypeUiEnum.standard,
                        label: 'Standard',
                        icon: 'pi pi-align-justify',
                        color: '#10b981',
                        backgroundColor: '#ecfdf5',
                        description: 'Standard'
                    },
                    {
                        key: CmContractInlineObjectViewTypeUiEnum.object,
                        label: 'Object',
                        icon: 'pi pi-align-justify',
                        color: '#10b981',
                        backgroundColor: '#ecfdf5',
                        description: 'Insurable Object'
                    }
                ]
            }
        ];
    }

    ngOnChanges(changes: SimpleChanges) {
        if (!this.initialized) {
            return;
        }
    }

    customFunction() {
        this.showCreateTicketOverlay = true;
        this.toggleAddTicket();
    }

    toggleAddTicket() {
        if (this.createTicketOverlay.overlayVisible) {
            this.createTicketOverlay.hide();
        } else {
            this.createTicketOverlay.toggle(event);
        }
    }

    handleTicketCreation(event: ObjectIdentifierData | ObjectIdentifier) {
        this.createTicketOverlay.hide();
        this.showCreateTicketOverlay = false;
    }

}
